import { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  ButtonPrimary,
  ButtonPseudo,
  ResponseError,
  responseErrorsResolver,
  SidePanel,
  SidePanelLoader,
  SidePanelType,
  TransactionSignatures,
  // IconDown,
  IconAnonymized,
  Table,
  formatDate,
  formatBalance,
  IconAttention,
  ButtonSecondary,
  Popup,
  SignaturePopup,
  usePrevious,
  BlacklistDataContent,
  BlacklistData,
  TransactionWarning,
  BaseellaStatus,
  PermissionEnum,
  PermissionDenied,
  IconArrowRight,
  BlacklistUserInfo,
  BlacklistUserInfoData,
  TransactionWarningGroups,
  IconLoader,
  formatBlacklistItem,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetPaymentsOrderQuery,
  useLazyGetPaymentsOrderStatusesQuery,
  useUpdatePaymentOrderMutation,
  useUpdatePaymentOrderStatusMutation,
  useLazyGetPaymentsOrderSignaturesQuery,
  useLazyGetPaymentOrderDocumentFileQuery,
  useLazyGetPaymentsOrderExtendedQuery,
  useCreateCashCollectionSignatureMutation,
  useLazyGetPaymentsOrderWarningsQuery,
  useBatchApplyWarningActionMutation,
  useApplyWarningActionMutation,
  useResetPaymentOrderTransactionWarningActionMutation,
  useLazyGetPaymentsOrderWarningsWithPaginationQuery,
  useLazyGetPaymentsOrderWarningQuery,
  useLazyGetWarningGroupBeneficiariesQuery,
  useLazyGetPaymentOrderWarningsInfoQuery,
} from './store/services';
import { clearPaymentOrderState } from './store/slices/list';
import PaymentOrderStatuses from '../../components/cash-collection/statuses';
import PaymentOrderGeneral from '../../components/cash-collection/general-info';
import {
  PaymentOrderFormValues,
  PaymentOrder,
  Order,
  WarningGroup,
} from './types';
import useGetAccountsAutocompleteList from './use-accounts-autocomplete';
import useGetPspPartnersAutocompleteList from '../../store/dictionaries/helpers/use-psp-partners-autocomplete';
import useGetPrimaryAccountsAutocompleteList from '../../store/dictionaries/helpers/use-primary-accounts-autocomplete';
import { useTransactionsUploadMutation } from '../transactions/store/services';
import { UploadResponse } from '../transactions/type';
import { columnsItems, columnsLabels } from './variables/statements';
import { MfaToken, MfaType } from '../../store/types';
import {
  useSendCodeMutation,
  useValidateCodeMutation,
} from '../../store/signature/service';
import { Account } from '../../types/account';
import { useCheckDocumentStringNameMutation } from '../../../../../store/service';

import cn from './styles.module.css';

import ROUTES from '../../routes';

let timer: NodeJS.Timeout;
let ccTimerId: NodeJS.Timeout;
let ccStatusRequest: any;

const formatOrdersList = (list: Order[]): Order[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, any> = {
      orderDate: <div>{formatDate(result.orderDate || '', true)}</div>,
      senderDob: (
        <div>
          {result.senderDob === '%Anonymized%' ? (
            <IconAnonymized />
          ) : (
            formatDate(result.senderDob)
          )}
        </div>
      ),
      senderName: (
        <div>
          {result.senderName === '%Anonymized%' ? (
            <IconAnonymized />
          ) : (
            result.senderName
          )}
        </div>
      ),
      beneficiaryName: (
        <div>
          {result.beneficiaryName === '%Anonymized%' ? (
            <IconAnonymized />
          ) : (
            result.beneficiaryName
          )}
        </div>
      ),
      localCountryId: <div>{result.localCountry.name}</div>,
      localCurrencyId: <div>{result.localCurrency.name}</div>,
      localAmount: (
        <div>
          {formatBalance(Number(result.localAmount), result?.localCurrency)}
        </div>
      ),
      destCountryId: <div>{result?.destCountry.name}</div>,
      destCurrencyId: <div>{result.destCurrency.name}</div>,
      destAmount: (
        <div>
          {formatBalance(Number(result.destAmount), result?.destCurrency)}
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const CashCollectionTransaction = ({ backPath }: { backPath: string }) => {
  const [panelsIsOpen, setPanelIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    'gl' | 'signings' | 'warnings' | 'orders' | 'warnings'
  >('gl');
  const [errorsMessage, setErrorMessage] = useState('');
  const [uploadId, setUploadId] = useState('');
  const [uploadedFilesError, setUploadedFilesError] = useState<string[]>([]);
  const [receiptUploadId, setReceiptUploadId] = useState('');
  const [rejectPopupVisible, setRejectPopupVisible] = useState(false);
  const [signaturePopupVisible, setSignaturePopupVisible] = useState(false);
  const [errorMessageSignature, setErrorMessageSignature] = useState('');
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [applyError, setApplyError] = useState('');
  const [statusPopupVisible, setStatusPopupVisible] = useState(false);
  const [preparedStatus, setPreparedStatus] = useState('');
  const [selectedWarningId, setSelectedWarningId] = useState('');
  const [selectedWarningGroup, setSelectedWarningGroup] = useState('');
  const [uploadedReceiptDocumentsErrors, setUploadedReceiptDocumentsErrors] =
    useState<{ name: string; error: string }[]>([]);
  const [internalError, setInternalError] = useState('');
  const [selectedMonitoringRuleWarningId, setSelectedMonitoringRuleWarningId] =
    useState('');
  const [confirmationPopupVisible, setConfirmationPopupVisible] =
    useState(false);

  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const { transactionId } = useParams();

  const {
    cashCollection: {
      list: {
        transaction,
        statuses,
        signatures,
        orders,
        warnings,
        warningsInfo,
        errorsList,
      },
    },
    transactionsDictionaries: {
      currencies,
      activePermissions,
      transactionsTypes,
    },
    transactions: { currentEmployee },
    signatures: { mfaToken },
  } = useAppSelector((state) => state);
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { search } = useLocation();

  const prevWarnings = usePrevious(warnings);

  const {
    control,
    formState: { errors, dirtyFields },
    setError,
    reset,
    setValue,
    watch,
    getValues,
  } = useForm<PaymentOrderFormValues>();

  const isDirty = Object.keys(dirtyFields).length !== 0;

  const fields = watch();

  const pspPartnerId = watch('pspPartnerId');
  const currencyId = watch('currencyId');

  const prevPspPartnerId = usePrevious(pspPartnerId);
  const prevCurrencyId = usePrevious(currencyId);

  const [sendCode, { isLoading: sendCodeInProgress }] = useSendCodeMutation();
  const [validateCode, { isLoading: validateCodeInProgress }] =
    useValidateCodeMutation();
  const [createSignature, { isLoading: createSignatureInProgress }] =
    useCreateCashCollectionSignatureMutation();
  const [
    getPaymentOrderWarning,
    {
      isLoading: paginationInProgress,
      isFetching: paginationIsFetching,
      isSuccess: paginationIsSuccess,
    },
  ] = useLazyGetPaymentsOrderWarningsWithPaginationQuery();

  const [
    getTransactionDocumentFile,
    {
      isLoading: downloadInProgress,
      isFetching: downloadIsFetching,
      isSuccess: downloadFileIsSuccess,
    },
  ] = useLazyGetPaymentOrderDocumentFileQuery();
  const [getPaymentsOrder, { isLoading, isFetching }] =
    useLazyGetPaymentsOrderQuery();
  const [getPaymentsOrderWarnings, { isLoading: warningsIsLoading }] =
    useLazyGetPaymentsOrderWarningsQuery();
  const [
    getPaymentsOrderSignatures,
    { isLoading: signaturesIsLoading, isFetching: signaturesIsFetching },
  ] = useLazyGetPaymentsOrderSignaturesQuery();
  const [getPaymentsOrderExtended] = useLazyGetPaymentsOrderExtendedQuery();
  const [upload, { isLoading: fileUploadInProgress }] =
    useTransactionsUploadMutation();
  const [checkDocumentName, { isLoading: checkDocumentNameInProgress }] =
    useCheckDocumentStringNameMutation();
  const [
    getWarningGroupBeneficiaries,
    { isLoading: beneficiariesIsLoading, isFetching: beneficiariesIsFetching },
  ] = useLazyGetWarningGroupBeneficiariesQuery();

  const [
    batchApplyWarning,
    {
      isLoading: batchApplyWarningInProgress,
      isSuccess: batchApplyWarningIsSuccess,
    },
  ] = useBatchApplyWarningActionMutation();
  const [
    resetPaymentOrderTransactionWarning,
    {
      isLoading: resetPaymentOrderTransactionActionIsLoading,
      isSuccess: resetPaymentOrderTransactionActionIsSuccess,
    },
  ] = useResetPaymentOrderTransactionWarningActionMutation();
  const [applyWarning, { isLoading: applyWarningInProgress }] =
    useApplyWarningActionMutation();
  const [blacklistDataVisible, setBlacklistDataVisible] =
    useState<boolean>(false);
  const [blacklistData, setBlacklistData] = useState<BlacklistData | undefined>(
    undefined,
  );
  const [blacklistItem, setBlacklistDataItem] =
    useState<BlacklistUserInfoData>();
  const [blacklistItemVisible, setBlacklistItemVisible] = useState(false);

  const [
    getStatuses,
    { isLoading: statusesIsLoading, isFetching: statusesIsFetching },
  ] = useLazyGetPaymentsOrderStatusesQuery();

  const [
    getPaymentOrderWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetPaymentsOrderWarningQuery();

  const [getPaymentOrderWarningsInfo, { isLoading: warningsInfoIsLoading }] =
    useLazyGetPaymentOrderWarningsInfoQuery();

  const {
    autocompleteList: recipientAccounts,
    onSearch: onSearchRecipientAccounts,
    isLoading: recipientAccountsIsLoading,
    setAutocompleteList,
    setAccountsList,
  } = useGetAccountsAutocompleteList(
    `&isClientAccount=true${!transactionId ? '&closed=false' : ''}`,
    transactionId,
  );

  const {
    autocompleteList: pspPartners,
    onSearch: onSearchPspPartners,
    isLoading: pspPartnersIsLoading,
  } = useGetPspPartnersAutocompleteList('&paymentsTypes=SWIFT', true);

  const {
    autocompleteList: pspPartnerAccounts,
    onSearch: onSearchPspPartnerAccounts,
    isLoading: pspPartnerAccountsIsLoading,
    nextPageIsLoading,
    onScroll,
    pagination,
  } = useGetPrimaryAccountsAutocompleteList(pspPartnerId, currencyId, true);

  const [update, { isLoading: requestInProgress, isSuccess: updateIsSucess }] =
    useUpdatePaymentOrderMutation();
  const [updateStatus, { isLoading: statusUpdateInProgress, isSuccess }] =
    useUpdatePaymentOrderStatusMutation();

  const getWarningGroupBeneficiariesAction = async ({
    id,
    warningId,
    cursor,
  }: {
    id: string;
    warningId: string;
    cursor: string;
  }) => {
    setSelectedWarningId(warningId);

    await getWarningGroupBeneficiaries({
      poId: transactionId as string,
      id,
      warningId,
      cursor,
    });
  };

  const getPaymentOrderWarningsAction = async ({
    warningGroupId,
    cursor,
  }: {
    warningGroupId: string;
    cursor: string;
  }) => {
    setSelectedWarningGroup(warningGroupId);

    await getPaymentOrderWarning({
      id: transactionId as string,
      warningGroupId,
      cursor,
    });
  };

  const getPaymentOrderWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    setSelectedMonitoringRuleWarningId(warningId);

    const response = await getPaymentOrderWarningById({
      poId: transactionId as string,
      warningId,
      id,
    });

    if (
      !blacklistData &&
      !blacklistItem &&
      !blacklistDataVisible &&
      !blacklistItemVisible &&
      response
    ) {
      if (response?.data?.data?.blacklistDoc) {
        setBlacklistData(response?.data?.data?.blacklistDoc);
        setBlacklistDataVisible(true);
      } else if (response?.data?.data?.blacklistItem) {
        setBlacklistDataItem(
          formatBlacklistItem(response?.data?.data?.blacklistItem),
        );
        setBlacklistItemVisible(true);
      }
    }
  };

  const onRetry = useCallback(async () => {
    ccStatusRequest = getStatuses({
      id: transactionId as string,
      statusPrev: transaction?.status as string,
    });

    const response = await ccStatusRequest.unwrap();

    ccTimerId = setTimeout(async () => {
      ccStatusRequest?.abort();

      clearTimeout(ccTimerId);

      if (
        transaction?.status !== 'REJECTED' &&
        transaction?.status !== 'ACCEPTED' &&
        transaction?.status !== 'AML_REJ' &&
        response.data
      ) {
        onRetry();
      } else {
        ccStatusRequest?.abort();

        clearTimeout(ccTimerId);
      }
    }, 5000);
  }, [transaction]);

  const onResetTransactionWarningAction = async (
    warningId: string,
    id: string,
  ) => {
    setSelectedWarningId(warningId);

    const response = (await resetPaymentOrderTransactionWarning({
      poId: transactionId as string,
      id,
      warningId,
    })) as { error: ResponseError };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setApplyError,
      });
    } else {
      const warningsArray: TransactionWarning[] = [];

      await getPaymentOrderWarningsInfo({
        id: transactionId as string,
        query: `warningGroupId=${warnings
          ?.map((item) => item.id)
          ?.join('&warningGroupId=')}`,
      });

      warnings?.forEach((item) => {
        item.warnings.edges.forEach((warning) => {
          warningsArray.push(warning.node);
        });
      });

      const nextWarnings = warningsArray.filter(
        (item) => !item.action && item.id !== warningId,
      );

      if (!nextWarnings?.length) {
        await onRetry();
      }
    }
  };

  const applyWarningAction = async ({
    id,
    warningId,
    action,
    comment,
    matchStatus,
  }: {
    id: string;
    warningId: string;
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const response = (await applyWarning({
      poId: transactionId as string,
      id,
      warningId,
      body: {
        action,
        comment,
        employeeId: currentEmployee?.id as string,
        matchStatus,
      },
    })) as { error: ResponseError };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setApplyError,
      });
    } else {
      const warningsArray: TransactionWarning[] = [];

      await getPaymentOrderWarningsInfo({
        id: transactionId as string,
        query: `warningGroupId=${warnings
          ?.map((item) => item.id)
          ?.join('&warningGroupId=')}`,
      });

      warnings?.forEach((item) => {
        item.warnings.edges.forEach((warning) => {
          warningsArray.push(warning.node);
        });
      });

      const nextWarnings = warningsArray.filter(
        (item) => !item.action && item.id !== warningId,
      );

      if (!nextWarnings?.length) {
        await onRetry();
      }
    }
  };

  const batchApplyWarningAction = async ({
    action,
    comment,
    matchStatus,
  }: {
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const ongoingGroupsWithoutWanings: WarningGroup[] = [];

    warnings?.forEach((item) => {
      if (item?.warnings?.edges?.find((warning) => !warning?.node?.action)) {
        ongoingGroupsWithoutWanings.push(item);
      }
    });

    if (ongoingGroupsWithoutWanings && ongoingGroupsWithoutWanings.length) {
      const responseBatch = await Promise.all(
        ongoingGroupsWithoutWanings?.map(async (item) => {
          const response = (await batchApplyWarning({
            poId: transactionId as string,
            body: {
              warningGroupId: item.id,
              action,
              employeeId: currentEmployee?.id as string,
              comment,
              matchStatus,
            },
          })) as { error: ResponseError };
          if (response.error) {
            responseErrorsResolver({
              errors: response.error,
              setError: () => {},
              setGlobalError: setApplyError,
            });
          }
          return response;
        }),
      );

      if (responseBatch) {
        await getPaymentOrderWarningsInfo({
          id: transactionId as string,
          query: `warningGroupId=${warnings
            ?.map((item) => item.id)
            ?.join('&warningGroupId=')}`,
        });

        await onRetry();
      }
    }
  };

  const getPaymentsOrderAction = async (id: string) => {
    setInternalError('');

    const response = (await getPaymentsOrder(id)) as { error?: ResponseError };

    if (response?.error) {
      if (response?.error?.status === 400) {
        if (Array?.isArray(response?.error?.data?.message)) {
          if (response?.error?.data?.message[0]) {
            setInternalError(response?.error?.data?.message[0].value as string);
          }
        } else if (typeof response?.error?.data?.message === 'string') {
          setInternalError(response?.error?.data?.message);
        } else {
          setInternalError('Internal Server Error. Try Again');
        }
      } else {
        setInternalError('Internal Server Error. Try Again');
      }
    } else {
      getPaymentsOrderSignatures(id);
      getPaymentsOrderWarnings(id);
    }
  };

  const onSendCode = async () => {
    clearTimeout(timer);

    const response = (await sendCode({ mfa: MfaType.TOTP })) as {
      data: { data: MfaToken };
    };

    if (response && response.data && response.data.data) {
      setSignaturePopupVisible(true);

      const expireTime = Math.round(
        new Date(response.data.data.expiresAt).getTime() - Date.now(),
      );

      timer = setTimeout(() => {
        onSendCode();
      }, expireTime);
    }
  };

  const validateCodeAction = async (code: string) => {
    await clearTimeout(timer);
    setErrorMessageSignature('');

    if (code) {
      const response = (await validateCode({
        mfaToken: mfaToken as string,
        totpToken: code,
      })) as {
        data?: { data: MfaToken };
        error: { data: { message: string; statusCode: number } };
      };

      if (response && response.data && response.data.data) {
        const createResponse = (await createSignature({
          id: transactionId as string,
          body: {
            mfaToken: response.data.data.token,
            signedAt: new Date().toISOString(),
          },
        })) as {
          error: { data: { message: string }; status: number };
        };

        if (
          createResponse &&
          createResponse.error &&
          createResponse.error.data
        ) {
          if (createResponse.error?.status === 403) {
            setErrorMessageSignature('Access denied');
          } else {
            responseErrorsResolver({
              errors: response.error as ResponseError,
              setError: () => {},
              setGlobalError: setErrorMessageSignature,
            });
          }
        } else {
          setSignaturePopupVisible(false);
          setErrorMessageSignature('');
          getPaymentsOrderSignatures(transaction?.id as string);

          await onRetry();
        }
      } else if (response && response.error && response.error.data) {
        if (response.error.data.statusCode === 401) {
          setErrorMessageSignature('Incorrect OTP code');
        } else {
          responseErrorsResolver({
            errors: response.error as ResponseError,
            setError: () => {},
            setGlobalError: setErrorMessageSignature,
          });
        }
      }
    } else {
      setErrorMessageSignature('OTP Code should not be empty');
    }
  };

  const onUploadReceipt = async (files: FileList) => {
    setUploadedFilesError([]);
    setUploadedReceiptDocumentsErrors([]);

    const nextUploadedFilesErrors: string[] = [];
    const nextUploadedDocumentsErrors: { name: string; error: string }[] = [];

    const filesResponse = await Promise.all(
      Object.keys(files).map(async (file) => {
        const fileItem = files[file as any];

        const checkNameResponse = (await checkDocumentName({
          value: fileItem.name,
        })) as {
          error?: {
            data: {
              message?: {
                constraints: Record<string, string>;
              }[];
            };
          };
        };

        let nextFile;

        if (!checkNameResponse?.error) {
          const response = (await upload(files[file as any])) as unknown as {
            data?: UploadResponse[];
            error?: {
              originalStatus: number;
              data?: {
                message?: string;
              };
            };
            message?: string;
          };

          if (response && response.data) {
            nextFile = { ...response.data[0] };

            let fileName = fileItem?.name;

            if (
              response.data[0] &&
              response.data[0]?.mimeType?.includes('image')
            ) {
              fileName = `${fileItem?.name.slice(0, fileItem?.name.lastIndexOf('.'))}.jpg`;
            }

            nextFile.name = fileName;
          } else if (response.error) {
            if (response.error.originalStatus === 413) {
              nextUploadedFilesErrors.push(
                `${files[file as any].name} size too large`,
              );
            } else {
              // eslint-disable-next-line no-lonely-if
              if (response?.error?.data?.message) {
                nextUploadedFilesErrors.push(response?.error?.data?.message);
              }
            }
          }
        } else if (
          checkNameResponse?.error &&
          checkNameResponse?.error?.data?.message
        ) {
          const errorItem =
            checkNameResponse?.error?.data?.message[0]?.constraints;

          if (errorItem) {
            nextUploadedDocumentsErrors.push({
              name: files[file as any].name,
              error: Object.values(errorItem)[0],
            });
          }
        }

        return nextFile;
      }),
    );

    setUploadedFilesError(nextUploadedFilesErrors);
    setUploadedReceiptDocumentsErrors(nextUploadedDocumentsErrors);

    if (filesResponse && filesResponse.length && filesResponse[0]) {
      const nextReceiptUploadId = filesResponse[0]?.id;
      setReceiptUploadId(nextReceiptUploadId);

      const response = (await update({
        id: transactionId as string,
        body: {
          currencyId: transaction?.currencyId,
          receiptUploadId: nextReceiptUploadId,
        },
      })) as { error: ResponseError };

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError: setErrorMessage,
        });
      }
    }

    if (uploadInputRef && uploadInputRef.current) {
      uploadInputRef.current.value = '';
    }
  };

  const onDownloadDocument = async (id: string, type: 'list' | 'receipt') => {
    setUploadId(id);

    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      uploadId: id,
    });

    if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download =
        type === 'list' ? 'Compliance batch.xlsx' : 'Receipt';
      hiddenElement.click();
    }
  };

  const updateStatusAction = async (status: string) => {
    setErrorMessage('');

    if (isDirty) {
      setPreparedStatus(status);
      setStatusPopupVisible(true);
    } else if (!isDirty) {
      if (
        status === BaseellaStatus.REJECTED ||
        status === BaseellaStatus.AML_REJ
      ) {
        setPreparedStatus(status);
        setStatusPopupVisible(false);
        setRejectPopupVisible(true);
      } else {
        ccStatusRequest?.abort();

        clearTimeout(ccTimerId);

        const response = (await updateStatus({
          id: transactionId as string,
          body: {
            status,
            currencyId: transaction?.currencyId as string,
          },
        })) as { data?: PaymentOrder; error?: ResponseError };

        if (response && response?.data) {
          if (rejectPopupVisible) {
            setRejectPopupVisible(false);
          }

          if (status === 'ACCEPTED' || status === 'REJECTED') {
            getPaymentsOrder(transactionId as string);
          } else {
            await onRetry();
          }
        } else if (response.error) {
          responseErrorsResolver({
            errors: response.error,
            setError: () => {},
            setGlobalError: setErrorMessage,
          });
        }
      }
    }
  };

  const onSignatureCancelClick = () => {
    clearTimeout(timer);
    setSignaturePopupVisible(false);
  };

  const onSubmit = async () => {
    const values = getValues();

    setErrorMessage('');

    const body: Record<string, string | number> = {
      sum: Number(values.sum),
      currencyId: values.currencyId as string,
      orderCreatedAt: values.orderCreatedAt as string,
      recepientAccountId: values.recepientAccountId as string,
    };

    if (values.orderNumber) {
      body.orderNumber = values.orderNumber;
    }

    if (values.pspPartnerAccountId) {
      body.pspPartnerAccountId = values.pspPartnerAccountId;
    }

    if (values.pspPartnerId) {
      body.pspPartnerId = values.pspPartnerId;
    }

    const response = (await update({
      id: transactionId as string,
      body,
    })) as {
      data?: PaymentOrder;
      error: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError: setErrorMessage,
      });
    } else if (response.data && !preparedStatus) {
      await onRetry();
    }

    return response;
  };

  const onChangeStatusPopup = async () => {
    ccStatusRequest?.abort();

    clearTimeout(ccTimerId);

    setErrorMessage('');

    if (preparedStatus) {
      const values = getValues();

      const body: Record<string, string | number> = {
        sum: Number(values.sum),
        currencyId: values.currencyId as string,
        orderCreatedAt: values.orderCreatedAt as string,
        recepientAccountId: values.recepientAccountId as string,
      };

      if (values.orderNumber) {
        body.orderNumber = values.orderNumber;
      }

      if (values.pspPartnerAccountId) {
        body.pspPartnerAccountId = values.pspPartnerAccountId;
      }

      if (values.pspPartnerId) {
        body.pspPartnerId = values.pspPartnerId;
      }

      const saveResponse = (await update({
        id: transactionId as string,
        body,
      })) as {
        data?: PaymentOrder;
        error: ResponseError;
      };

      if (saveResponse && saveResponse.data) {
        if (
          preparedStatus === BaseellaStatus.REJECTED ||
          preparedStatus === BaseellaStatus.AML_REJ
        ) {
          setStatusPopupVisible(false);
          setRejectPopupVisible(true);
        } else {
          const response = (await updateStatus({
            id: transactionId as string,
            body: {
              status: preparedStatus,
              currencyId: transaction?.currencyId as string,
            },
          })) as { data?: PaymentOrder; error?: ResponseError };

          if (response && response?.data) {
            ccStatusRequest?.abort();

            clearTimeout(ccTimerId);

            if (preparedStatus === 'ACCEPTED') {
              getPaymentsOrder(transactionId as string);
            } else {
              await onRetry();
            }
          } else if (response.error) {
            responseErrorsResolver({
              errors: response.error,
              setError,
              setGlobalError: setErrorMessage,
            });
          }
        }
      } else if (saveResponse.error) {
        responseErrorsResolver({
          errors: saveResponse.error,
          setError,
          setGlobalError: setErrorMessage,
        });
      }
    }
  };

  const formattedData = useMemo(() => {
    const data = formatOrdersList(orders as Order[]);

    return data;
  }, [orders]);

  const onReject = async () => {
    ccStatusRequest?.abort();

    clearTimeout(ccTimerId);

    const response = (await updateStatus({
      id: transactionId as string,
      body: {
        status: preparedStatus || 'REJECTED',
        currencyId: transaction?.currencyId as string,
      },
    })) as { data?: PaymentOrder; error?: ResponseError };

    if (response && response?.data) {
      if (rejectPopupVisible) {
        setRejectPopupVisible(false);
        setPreparedStatus('');
      }
      getPaymentsOrder(transactionId as string);
    } else if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError: setErrorMessage,
      });
    }
  };

  const handleClose = useCallback(async () => {
    ccStatusRequest?.abort();

    await clearTimeout(ccTimerId);

    setUploadId('');
    setUploadedFilesError([]);
    setErrorMessage('');
    setActiveTab('gl');
    setErrorMessageSignature('');
    setUploadedReceiptDocumentsErrors([]);
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(`${backPath}${search || ''}`);
      dispatch(clearPaymentOrderState());
    }, 100);
  }, []);

  useEffect(() => {
    setPanelIsOpen(true);

    if (
      transactionId &&
      activePermissions?.includes(PermissionEnum.PAYMENTS_ORDERS_LIST)
    ) {
      getPaymentsOrderAction(transactionId);
    }
  }, [transactionId, activePermissions]);

  useEffect(() => {
    if (warnings && warnings?.length) {
      getPaymentOrderWarningsInfo({
        id: transactionId as string,
        query: `warningGroupId=${warnings
          ?.map((item) => item.id)
          ?.join('&warningGroupId=')}`,
      });
    }
  }, [warnings, prevWarnings]);

  useEffect(() => {
    if (warningsInfo && warningsInfo?.entitySummary?.unprocessed > 0) {
      setActiveTab('warnings');
    }
  }, [warningsInfo]);

  useEffect(() => {
    if (transaction) {
      if (transaction?.recepientAccount) {
        setAccountsList([transaction.recepientAccount as Account]);
        setAutocompleteList([
          {
            name: transaction?.recepientAccount.description,
            description: '',
            value: transaction?.recepientAccount.id,
            suffix: transaction?.recepientAccount.fullNumber,
          },
        ]);
      }

      const body = {
        orderCreatedAt: transaction.orderCreatedAt || transaction?.createdAt,
        sum: transaction?.sum?.toString(),
        currencyId: transaction?.currencyId,
        recepientAccountId: transaction?.recepientAccountId,
        pspPartnerId: transaction?.pspPartnerId || '',
        pspPartnerAccountId: transaction?.pspPartnerAccountId || '',
        orderNumber: transaction?.orderNumber || '',
      };

      setReceiptUploadId(transaction?.receiptUploadId || '');

      reset(body);

      if (
        transaction.status !== 'ACCEPTED' &&
        transaction.status !== 'REJECTED'
      ) {
        onRetry();
      }
    }
  }, [transaction]);

  useEffect(() => {
    if (downloadFileIsSuccess) {
      setUploadId('');
    }
  }, [downloadFileIsSuccess]);

  useEffect(() => {
    if (
      prevPspPartnerId !== undefined &&
      prevPspPartnerId !== null &&
      ((pspPartnerId && pspPartnerId !== prevPspPartnerId) ||
        (prevPspPartnerId && !pspPartnerId))
    ) {
      setValue('pspPartnerAccountId', '');
    }
  }, [pspPartnerId, prevPspPartnerId]);

  useEffect(() => {
    if (currencyId && prevCurrencyId && prevCurrencyId !== currencyId) {
      setValue('pspPartnerId', '');
      setValue('pspPartnerAccountId', '');
    }
  }, [currencyId, prevCurrencyId]);

  useEffect(() => {
    if (activeTab === 'orders' && transactionId) {
      getPaymentsOrderExtended(transactionId);
    }
  }, [activeTab, transactionId]);

  useEffect(() => {
    if (paginationIsSuccess) {
      setSelectedWarningGroup('');
    }
  }, [paginationIsSuccess]);

  useEffect(() => {
    if (updateIsSucess) {
      setConfirmationPopupVisible(false);
    }
  }, [updateIsSucess]);

  let content = <div />;

  if (
    (isLoading ||
      (!transaction && isFetching) ||
      signaturesIsLoading ||
      warningsIsLoading ||
      warningsInfoIsLoading ||
      (transaction && warnings && warnings?.length && !warningsInfo)) &&
    !internalError
  ) {
    content = <SidePanelLoader />;
  } else if (!internalError) {
    if (activePermissions?.includes(PermissionEnum.PAYMENTS_ORDERS_LIST)) {
      content = (
        <div className={cn.paymentsOrderWrapper}>
          {transaction &&
            !signatures?.length &&
            !transaction?.status.includes('REJ') &&
            !signaturesIsLoading &&
            !signaturesIsFetching && (
              <div className={cn.notSignedAlert}>
                <div className={cn.notSignedText}>
                  <IconAttention width={24} height={24} />
                  Additional signatures required
                </div>
                <div className={cn.notSignedControls}>
                  <ButtonPrimary
                    text={sendCodeInProgress ? 'Loading...' : 'Sign'}
                    onClick={onSendCode}
                    className={cn.signButton}
                    disabled={sendCodeInProgress}
                  />
                  <ButtonSecondary
                    text="Reject"
                    onClick={() => setRejectPopupVisible(true)}
                  />
                </div>
              </div>
            )}
          <PaymentOrderStatuses
            status={transaction?.status}
            statuses={statuses}
            disabled={statusesIsLoading || statusUpdateInProgress}
            onUpdate={updateStatusAction}
            isFetching={statusesIsFetching}
            isLoading={statusesIsLoading}
            isEditable={activePermissions?.includes(
              PermissionEnum.PAYMENTS_ORDERS_EDIT_ACTION,
            )}
          />
          <div className={cn.tabs}>
            <button
              type="button"
              className={`${cn.tab} ${activeTab === 'gl' ? cn.active : ''}`}
              onClick={() => {
                setActiveTab('gl');
              }}
            >
              General Info
            </button>
            <button
              type="button"
              className={`${cn.tab} ${activeTab === 'orders' ? cn.active : ''}`}
              onClick={() => {
                setActiveTab('orders');
              }}
            >
              Orders
            </button>
            <button
              type="button"
              className={`${cn.tab} ${
                activeTab === 'signings' ? cn.active : ''
              }`}
              onClick={() => {
                setActiveTab('signings');
              }}
            >
              Signatures
            </button>
            <button
              type="button"
              className={`${cn.tab} ${
                activeTab === 'warnings' ? cn.active : ''
              }`}
              onClick={() => {
                setActiveTab('warnings');
              }}
            >
              {`Warnings ${
                warningsInfo && warningsInfo?.entitySummary?.unprocessed > 0
                  ? `(${warningsInfo?.entitySummary?.unprocessed})`
                  : ''
              }`}
            </button>
          </div>
          {activeTab === 'gl' && (
            <PaymentOrderGeneral
              control={control}
              errors={errors}
              currency={transaction?.currency}
              currencies={currencies}
              recipientAccounts={recipientAccounts}
              recipientAccountsIsLoading={recipientAccountsIsLoading}
              onSearchRecipientAccounts={onSearchRecipientAccounts}
              pspPartners={pspPartners}
              pspPartnersIsLoading={pspPartnersIsLoading}
              onSearchPspPartners={onSearchPspPartners}
              pspAccountDisabled={!pspPartnerId}
              pspPartnerAccounts={pspPartnerAccounts}
              pspPartnerAccountsIsLoading={pspPartnerAccountsIsLoading}
              onSearchPspPartnerAccounts={onSearchPspPartnerAccounts}
              status={transaction?.status}
              confirmedAmount={transaction?.confirmedAmount}
              pendingAmount={transaction?.pendingAmount}
              rejectedAmount={transaction?.rejectedAmount}
              transactionId={transactionId}
              orderUploadId={transaction?.orderUploadId}
              receiptUploadId={receiptUploadId}
              setReceiptUploadId={setReceiptUploadId}
              uploadId={uploadId}
              downloadInProgress={downloadInProgress || downloadIsFetching}
              onDownload={onDownloadDocument}
              uploadInputRef={uploadInputRef}
              anonymized={transaction?.anonymizedAt}
              fileUploadInProgress={
                checkDocumentNameInProgress || fileUploadInProgress
              }
              isEditable={activePermissions?.includes(
                PermissionEnum.PAYMENTS_ORDERS_EDIT_ACTION,
              )}
              uploadedReceiptDocumentsErrors={uploadedReceiptDocumentsErrors}
              onUpload={onUploadReceipt}
              uploadedFilesError={uploadedFilesError}
              fields={fields}
              pspPartnerAccountsNextPageIsLoading={nextPageIsLoading}
              pspPartnerAccountsOnScroll={onScroll}
              pspPartnerAccountsPagination={pagination}
            />
          )}
          {activeTab === 'signings' && (
            <div>
              {signaturesIsLoading || signaturesIsFetching ? (
                <div className={cn.signaturesLoading}>
                  <IconLoader />
                </div>
              ) : (
                <TransactionSignatures signatures={signatures} />
              )}
            </div>
          )}
          {activeTab === 'warnings' && (
            <>
              {(warningsIsLoading || warningsInfoIsLoading) && (
                <div className={cn.signaturesLoading}>
                  <IconLoader />
                </div>
              )}
              {!warningsIsLoading && warningsInfo && (
                <TransactionWarningGroups
                  warningsGroups={warnings}
                  batchApplyWarning={batchApplyWarningAction}
                  batchApplyWarningInProgress={batchApplyWarningInProgress}
                  applyWarningInProgress={applyWarningInProgress}
                  applyWarning={applyWarningAction}
                  applyError={applyError}
                  setBlacklistData={setBlacklistData}
                  setBlacklistDataVisible={setBlacklistDataVisible}
                  setBlacklistDataItem={setBlacklistDataItem}
                  setBlacklistItemVisible={setBlacklistItemVisible}
                  setPanelIsOpen={setPanelIsOpen}
                  auditLogAvailable={activePermissions?.includes(
                    PermissionEnum.AUDIT_LOG_LIST,
                  )}
                  batchApplyWarningIsSuccess={batchApplyWarningIsSuccess}
                  onResetTransactionWarningAction={
                    onResetTransactionWarningAction
                  }
                  resetPaymentOrderTransactionActionIsLoading={
                    resetPaymentOrderTransactionActionIsLoading
                  }
                  resetPaymentOrderTransactionActionIsSuccess={
                    resetPaymentOrderTransactionActionIsSuccess
                  }
                  selectedWarningId={selectedWarningId}
                  getPaymentOrderWarningsAction={getPaymentOrderWarningsAction}
                  paginationInProgress={
                    paginationInProgress || paginationIsFetching
                  }
                  selectedWarningGroup={selectedWarningGroup}
                  getPaymentOrderWarningById={getPaymentOrderWarningByIdAction}
                  isWarningByIdLoading={isWarningByIdLoading}
                  isWarningByIdFetching={isWarningByIdFetching}
                  employee={currentEmployee}
                  activePermissions={activePermissions}
                  transactionsTypes={transactionsTypes}
                  getWarningGroupBeneficiaries={
                    getWarningGroupBeneficiariesAction
                  }
                  beneficiariesIsLoading={
                    beneficiariesIsLoading || beneficiariesIsFetching
                  }
                  warningsInfo={warningsInfo}
                  selectedMonitoringRuleWarningId={
                    selectedMonitoringRuleWarningId
                  }
                  hideApproveActions={
                    !activePermissions?.includes(
                      PermissionEnum.TRANSACTIONS_INCOMING_AML_APPROVE_ACTION,
                    )
                  }
                  hideRejectActions={
                    !activePermissions?.includes(
                      PermissionEnum.TRANSACTIONS_INCOMING_AML_APPROVE_ACTION,
                    )
                  }
                />
              )}
            </>
          )}
          {activeTab === 'orders' && (
            <div className={cn.paymentsOrderTableWrapper}>
              <div className={cn.paymentOrderTitle}>
                Cash collection payment order
                {activePermissions?.includes(
                  PermissionEnum.PAYMENTS_ORDERS_BENEFICIARIES_LIST,
                ) && (
                  <NavLink
                    to={`${ROUTES.CASH_COLLECTION_ORDERS}?paymentOrderId=${transactionId}`}
                    target="_blank"
                    className={cn.paymentOrdersBeneficiariesLink}
                  >
                    See all Orders in this Collection
                    <IconArrowRight width={12} height={12} />
                  </NavLink>
                )}
              </div>

              {!orders && (
                <div className={cn.signaturesLoading}>
                  <IconLoader />
                </div>
              )}
              {orders && (
                <Table
                  columns={columnsItems}
                  onFilterChange={() => {}}
                  data={formattedData || []}
                  onSort={() => {}}
                  filters={{}}
                  sort={{}}
                  columnsLabels={columnsLabels}
                  tableClassName={!orders ? cn.emptyStatements : ''}
                  error={errorsList}
                  withVerticalScroll
                  withScroll
                />
              )}
            </div>
          )}
        </div>
      );
    } else {
      content = <PermissionDenied />;
    }
  } else if (internalError) {
    content = <div className={cn.internalError}>{internalError}</div>;
  }

  useEffect(() => {
    if (isSuccess && statusPopupVisible) {
      setStatusPopupVisible(false);
    }
  }, [isSuccess, statusPopupVisible]);

  return (
    <>
      <SidePanel
        title="Cash Collection"
        isOpen={panelsIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        type={SidePanelType.Transactions}
        errorSidepanel={errorsMessage}
        sidepanelExpandedKey="cash-collection-panel"
        footer={
          <>
            <ButtonPrimary
              text="Save"
              disabled={requestInProgress}
              onClick={() => setConfirmationPopupVisible(true)}
              bold
            />
            <ButtonPseudo text="Close" onClick={handleClose} bold />
          </>
        }
        closeOnClickOutside
      >
        {content}
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistDataVisible}
        onClose={() => {
          setBlacklistDataVisible(false);
          setBlacklistData(undefined);
          setPanelIsOpen(true);
        }}
        title={blacklistData?.fullName as string}
        sidepanelExpandedKey="cash-collection-blacklist"
        footer={
          <ButtonPseudo
            text="Close"
            onClick={() => {
              setBlacklistDataVisible(false);
              setBlacklistData(undefined);
              setPanelIsOpen(true);
            }}
            bold
          />
        }
      >
        <BlacklistDataContent data={blacklistData} />
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistItemVisible}
        onClose={() => {
          setBlacklistDataItem(undefined);
          setBlacklistItemVisible(false);
          setPanelIsOpen(true);
        }}
        title="User Details"
        sidepanelExpandedKey="cash-collection-blacklist"
        footer={
          <ButtonPseudo
            text="Close"
            onClick={() => {
              setBlacklistDataItem(undefined);
              setBlacklistItemVisible(false);
              setPanelIsOpen(true);
            }}
            bold
          />
        }
        withoutExpand
        expanded
      >
        <BlacklistUserInfo info={blacklistItem} isLoading={false} />
      </SidePanel>
      <SignaturePopup
        signaturePopupVisible={signaturePopupVisible}
        validateCode={validateCodeAction}
        validateCodeInProgress={
          validateCodeInProgress || createSignatureInProgress
        }
        errorMessageSignature={errorMessageSignature}
        onSignatureCancelClick={onSignatureCancelClick}
      />
      <Popup
        title="The transaction will be rejected"
        onCancelClick={() => setRejectPopupVisible(false)}
        onOkClick={onReject}
        cancelButtonText="Cancel"
        okButtonText="Yes, Reject"
        isVisible={rejectPopupVisible}
        parent={document.getElementById('signature-popup') as HTMLElement}
        isLoading={statusUpdateInProgress}
      />
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('signature-popup') as HTMLElement}
      />
      <Popup
        title="You have unsaved changed"
        description="You want to change the status of a transaction, but you have unsaved changes on the transaction. Please select one of the following options:"
        onCancelClick={() =>
          setStatusPopupVisible && setStatusPopupVisible(false)
        }
        onOkClick={onChangeStatusPopup}
        isLoading={statusUpdateInProgress || requestInProgress}
        cancelButtonText="Cancel"
        okButtonText="Save changes and change status"
        isVisible={Boolean(statusPopupVisible)}
        parent={document.getElementById('signature-popup') as HTMLElement}
      />
      <Popup
        title="Are you sure to save all changes?"
        onCancelClick={() => setConfirmationPopupVisible(false)}
        onOkClick={onSubmit}
        isLoading={requestInProgress}
        cancelButtonText="Cancel"
        okButtonText={requestInProgress ? 'Loading...' : 'Save changes'}
        isVisible={confirmationPopupVisible}
        parent={document.getElementById('signature-popup') as HTMLElement}
        okButtonDisabled={requestInProgress}
      />
      <div id="signature-popup" />
      <div id="apply-all-popup" />
    </>
  );
};

export default CashCollectionTransaction;
